import { defineStore } from 'pinia'
import { z } from 'zod'
import { updateUserState } from '../../composables/third-party/useAssertiveYield'
import { useRuntimeConfig } from '#imports'
import { localStorage } from '../../utils/storage'
export const userEngagementSchema = z.object({
  isEngaged: z.boolean(),
  lastEngagementDate: z.date().nullable(),
})

export type UserEngagement = z.infer<typeof userEngagementSchema>

export const USER_TRACKING_STORAGE_KEY = 'app:userEngagement'

const defaultUserEngagement: UserEngagement = userEngagementSchema.parse({
  isEngaged: false,
  lastEngagementDate: null,
})

export const userEngagementStore = defineStore(USER_TRACKING_STORAGE_KEY, {
  state: () => ({ ...defaultUserEngagement }),

  getters: {
    isEnabled(): boolean {
      const enableUserTracking = useRuntimeConfig().public.enableUserTracking
      return Boolean(enableUserTracking)
    },
    localStorageEngagement(): UserEngagement {
      const storedEngagement = localStorage.getItem(USER_TRACKING_STORAGE_KEY)
      if (!storedEngagement) return defaultUserEngagement

      const parsedStoredEngagement = JSON.parse(storedEngagement)

      const validation = userEngagementSchema.safeParse({
        ...parsedStoredEngagement,
        lastEngagementDate: new Date(parsedStoredEngagement.lastEngagementDate),
      })

      if (!validation.success) {
        console.error('Invalid user engagement data:', validation.error.errors)
        return defaultUserEngagement
      }

      return validation.data
    },
  },

  actions: {
    loadUserEngagement() {
      if (!this.isEnabled) return
      this.setupLocalStorageSync()

      const storedEngagement = this.localStorageEngagement
      this.$patch({ ...storedEngagement })

      if (this.shouldResetEngagement()) {
        this.resetUserEngagement()
      }
    },

    setupLocalStorageSync() {
      if (!this.isEnabled) return
      this.$subscribe(
        async (_, state) => {
          localStorage.setItem(USER_TRACKING_STORAGE_KEY, JSON.stringify(state))
          await updateUserState(state.isEngaged)
        },
        { flush: 'sync' }
      )
    },

    setUserEngagement(
      key: keyof UserEngagement,
      value: UserEngagement[typeof key]
    ) {
      if (!this.isEnabled) return
      const updatedState = {
        ...this.$state,
        [key]: value,
        lastEngagementDate: new Date(),
      }

      const validation = userEngagementSchema.safeParse(updatedState)

      if (!validation.success) {
        console.error('Invalid user engagement entry', validation.error.errors)
        return
      }

      this.$patch(validation.data)
    },

    resetUserEngagement(): void {
      if (!this.isEnabled) return
      this.$patch({ ...defaultUserEngagement })
    },

    shouldResetEngagement(): boolean {
      return (
        !this.$state.isEngaged ||
        (this.$state.isEngaged && !this.isEngagementRecent())
      )
    },

    isEngagementRecent(): boolean {
      const lastEngagementDate = this.$state.lastEngagementDate
        ? new Date(this.$state.lastEngagementDate)
        : null

      if (!lastEngagementDate) return false

      const ONE_WEEK_IN_MS = 1000 * 60 * 60 * 24 * 7
      return (
        new Date().getTime() - lastEngagementDate.getTime() <= ONE_WEEK_IN_MS
      )
    },
  },
})
